import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import type { ScoutActiveTurn, ScoutChatMessage, ScoutThreadSummary } from "./types";

import { type ScoutConversationControllerSnapshot } from "./scoutConversationController";
import { useScoutConversationController } from "./useScoutConversationController";

type UseScoutConversationOptions = {
  tenantId: string | undefined;
  conversationId: string | undefined;
  onConversationIdChange?: (conversationId: string) => void;
};

type SendScoutMessageOptions = {
  conversationId?: string;
};

type UseScoutConversationResult = {
  conversation: ScoutThreadSummary | undefined;
  conversationId: string | undefined;
  conversations: ScoutThreadSummary[];
  messages: ScoutChatMessage[];
  activeTurn: ScoutActiveTurn | undefined;
  loading: boolean;
  error: Error | undefined;
  streamStatus: string | undefined;
  isSending: boolean;
  isAborting: boolean;
  sendMessage: (content: string, options?: SendScoutMessageOptions) => Promise<string | undefined>;
  abortTurn: () => Promise<boolean>;
  refetch: () => Promise<void>;
};

const EMPTY_CONTROLLER_SNAPSHOT: ScoutConversationControllerSnapshot = {
  storeSnapshot: {
    conversationsById: {},
    messagesByConversationId: {},
    activeTurnsByConversationId: {},
    streamStatusByConversationId: {},
  },
  runtimesByConversationId: {},
  reconciledConversationIdsByConversationId: {},
};

const EMPTY_RUNTIME_SNAPSHOT = {
  loading: false,
  error: undefined,
  isSending: false,
  isAborting: false,
};

/**
 * React-facing adapter over the shared Scout controller + store.
 *
 * The hook owns only view selection concerns:
 * - which conversation this consumer is currently displaying
 * - when to attach/detach that displayed conversation from the shared controller
 *
 * The hook does not own Scout's runtime state machine. History loading, SSE,
 * optimistic reconciliation, and abort handling all live in the controller.
 */
export function useScoutConversation({
  tenantId,
  conversationId,
  onConversationIdChange,
}: UseScoutConversationOptions): UseScoutConversationResult {
  const controller = useScoutConversationController();
  const controllerSnapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    () => EMPTY_CONTROLLER_SNAPSHOT
  );
  const storeSnapshot = controllerSnapshot.storeSnapshot;
  const [displayConversationId, setDisplayConversationId] = useState(conversationId);

  // Route changes still drive the local display target for this hook instance.
  useEffect(() => {
    setDisplayConversationId(conversationId);
  }, [conversationId]);

  // Attaches just the conversation this hook is currently rendering to the
  // shared controller. Other hook instances can attach other conversations at
  // the same time.
  useEffect(() => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    controller.attachConversation({
      tenantId,
      conversationId: displayConversationId,
    });

    return () => {
      controller.detachConversation(displayConversationId);
    };
  }, [controller, displayConversationId, tenantId]);

  // During first-send optimistic startup, the controller may have already moved
  // state from the optimistic id to the persisted id before this hook has
  // updated its local display id. This alias keeps rendering stable through that
  // handoff.
  const effectiveConversationId =
    (displayConversationId
      ? controllerSnapshot.reconciledConversationIdsByConversationId[displayConversationId]
      : undefined) ?? displayConversationId;

  const conversations = Object.values(storeSnapshot.conversationsById)
    .filter((conversation) => !tenantId || conversation.tenantId === tenantId)
    .sort((first, second) => (second.updatedAt ?? "").localeCompare(first.updatedAt ?? ""));
  const conversation = effectiveConversationId
    ? storeSnapshot.conversationsById[effectiveConversationId]
    : undefined;
  const messages = effectiveConversationId
    ? (storeSnapshot.messagesByConversationId[effectiveConversationId] ?? [])
    : [];
  const activeTurn = effectiveConversationId
    ? storeSnapshot.activeTurnsByConversationId[effectiveConversationId]
    : undefined;
  const streamStatus = effectiveConversationId
    ? storeSnapshot.streamStatusByConversationId[effectiveConversationId]
    : undefined;
  const runtime = effectiveConversationId
    ? (controllerSnapshot.runtimesByConversationId[effectiveConversationId] ??
      EMPTY_RUNTIME_SNAPSHOT)
    : EMPTY_RUNTIME_SNAPSHOT;

  const sendMessage = useCallback(
    async (content: string, options?: SendScoutMessageOptions) => {
      if (!tenantId) {
        throw new Error("Scout requires a tenant before sending a message.");
      }

      const targetConversationId = options?.conversationId ?? displayConversationId;
      const { optimisticConversationId, completion } = controller.sendMessage({
        tenantId,
        conversationId: targetConversationId,
        content,
      });

      // For a brand new conversation, switch the UI immediately to the
      // optimistic conversation id so the user sees their message and the local
      // Thinking state without waiting for navigation.
      if (!targetConversationId) {
        setDisplayConversationId(optimisticConversationId);
      }

      const resolvedConversationId = await completion;
      if (resolvedConversationId && resolvedConversationId !== targetConversationId) {
        setDisplayConversationId(resolvedConversationId);
        onConversationIdChange?.(resolvedConversationId);
      }

      return resolvedConversationId;
    },
    [controller, displayConversationId, onConversationIdChange, tenantId]
  );

  const abortTurn = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return false;
    }

    return controller.abortTurn({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  const refetch = useCallback(async () => {
    if (!tenantId || !displayConversationId) {
      return;
    }

    await controller.refetchConversation({
      tenantId,
      conversationId: displayConversationId,
    });
  }, [controller, displayConversationId, tenantId]);

  return {
    conversation,
    conversationId: displayConversationId,
    conversations,
    messages,
    activeTurn,
    loading: runtime.loading,
    error: runtime.error,
    streamStatus,
    isSending: runtime.isSending,
    isAborting: runtime.isAborting,
    sendMessage,
    abortTurn,
    refetch,
  };
}
